import {
  addSeconds,
  differenceInMinutes,
  format,
  formatDistance,
  intervalToDuration,
  isSameDay,
  parseISO,
} from 'date-fns'
import memoizeOne from 'memoize-one'

import { HOUR_SECONDS } from '@hap/constants'
import { Timespan } from '@hap/utils'

import { isNumber } from './numberHelpers'
import { isParsableDate } from './typeHelpers'

export const SHORT_HAND_DATE_WITH_TIME_FORMAT = 'MMM d, yyyy hh:mm aa'

type BaseDateFormatFunction = (
  date?: string | Date | number | null,
  dateFormat?: string,
) => string | null

export const formatDate = memoizeOne<BaseDateFormatFunction>(
  (date?: string | Date | number | null, dateFormat = 'eee MMM d, yyyy') => {
    if (!isParsableDate(date)) return null
    const d = new Date(date)
    return format(d, dateFormat)
  },
)

export const parseStrDates = (
  timespan: Timespan.Info | { type: 'static'; start: string; end: string },
): Timespan.Info => {
  if (timespan.type === 'preset') return timespan
  return {
    type: 'static',
    start: typeof timespan.start === 'string' ? parseISO(timespan.start) : timespan.start,
    end: typeof timespan.end === 'string' ? parseISO(timespan.end) : timespan.end,
  }
}

export const formatDateTime = memoizeOne<BaseDateFormatFunction>(
  (date?: string | Date | number | null, dateFormat = 'eee MMM d, yyyy @ h:mm a') => {
    return formatDate(date, dateFormat)
  },
)

export const relativeDate = memoizeOne<BaseDateFormatFunction>(
  (date?: string | Date | number | null, dateFormat = "MMM d, yyyy 'at' hh:mmaaa") => {
    if (!date || date === 'null') return null
    const d = new Date(date)
    const today = new Date()
    if (differenceInMinutes(today, d) < 1) {
      return 'now'
    } else if (differenceInMinutes(today, d) < 30) {
      return `${formatDistance(d, today, { addSuffix: true })}`
    } else if (isSameDay(today, d)) {
      return `Today at ${format(d, 'h:mmaaa')}`
    } else {
      return format(d, dateFormat)
    }
  },
)

export const formatNumber = memoizeOne(
  (number?: Nullable<number>, fallback = '—'): string => {
    return !isNumber(number) || isNaN(number) || !isFinite(number)
      ? fallback
      : number.toString()
  },
)

export const formatIntervalToWords = memoizeOne(
  (startTime: Date | string, endTime: Date | string): string => {
    if (typeof startTime === 'string') startTime = new Date(startTime)
    if (typeof endTime === 'string') endTime = new Date(endTime)

    const duration = intervalToDuration({
      start: startTime,
      end: endTime,
    })

    let output = ''
    if (duration.days) output += `${duration.days}d `
    if (duration.hours) output += `${duration.hours}h `
    if (duration.minutes) output += `${duration.minutes}m `
    if (duration.seconds) output += `${duration.seconds}s `
    return output.trim()
  },
)

export function humanizeSeconds(seconds: number) {
  const hours = Math.floor(seconds / HOUR_SECONDS)
  const minutes = Math.floor((seconds % HOUR_SECONDS) / 60)
  const remainingSeconds = Math.floor(seconds % 60)

  const parts: string[] = []

  if (hours > 0) parts.push(`${hours}h`)
  if (minutes > 0) parts.push(`${minutes}m`)
  if (remainingSeconds > 0) parts.push(`${remainingSeconds}s`)

  return parts.join(' ') || '0s'
}

export const formatDuration = memoizeOne(
  (durationSecs?: Nullable<number | string>, timeFormat = 'mm:ss'): string => {
    durationSecs = Number(durationSecs)

    if (isNaN(durationSecs) || !isFinite(durationSecs)) return '—'

    if (durationSecs > 3599 && timeFormat === 'mm:ss') {
      const seconds = Math.round(durationSecs % 60)
      return `${Math.floor(durationSecs / 60)}:${seconds < 10 ? '0' + seconds : seconds}`
    }

    const helperDate = addSeconds(new Date(0), isNaN(durationSecs) ? 0 : durationSecs)
    return `${format(helperDate, timeFormat)}`
  },
)

export function formatDateBySeries(date: string, series?: string) {
  const parsedDate = parseISO(date)
  switch (series) {
    case 'hour':
      return format(parsedDate, 'MMM d h:mmaaa')
    case 'week':
      return format(parsedDate, 'MMM d yyyy')
    case 'month':
      return format(parsedDate, 'MMM yyyy')
    default:
      return format(parsedDate, 'MMM d')
  }
}

export const CURRENT_TIMEZONE = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch (e) {
    console.warn('Failed to detect user timezone, falling back to UTC', e)
    return 'UTC'
  }
})()

/**
 * Get the user's preferred first day of the week based on their OS locale settings.
 * Uses Intl.Locale.getWeekInfo() when available, with Monday as fallback.
 *
 * @returns 0-6 (0=Sunday, 1=Monday, etc.) matching date-fns convention
 */
export function getUserWeekStartsOn(): Timespan.DayOfWeek {
  try {
    const localeString = Intl.DateTimeFormat().resolvedOptions().locale
    const locale = new Intl.Locale(localeString)

    // getWeekInfo() returns { firstDay: 1-7 } where 7=Sunday
    // date-fns expects { weekStartsOn: 0-6 } where 0=Sunday
    const weekInfo = (locale as any).getWeekInfo?.()

    if (weekInfo?.firstDay) {
      // Convert: Intl 7 (Sunday) -> date-fns 0 (Sunday)
      // Intl 1-6 (Mon-Sat) -> date-fns 1-6 (Mon-Sat)
      return weekInfo.firstDay === 7 ? 0 : weekInfo.firstDay
    }
  } catch {
    // Browser doesn't support getWeekInfo()
  }

  return 1 // Default to Monday
}
